import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { object } from "yup/lib/locale";
import { Project, LoginResponse, User } from "./models";
import { NewCommentDto } from "./models/Comment";
import Solution from "./models/Solution";

interface IConfig extends AxiosRequestConfig {
  public?: boolean;
}

class Api {
  private instance: AxiosInstance;
  private bearerToken: string;

  constructor() {
    this.instance = axios.create({ baseURL: process.env.NEXT_PUBLIC_API_URL });
    this.instance.interceptors.request.use(this.addAuthHeader);
  }

  getInstance = () => this.instance;

  setBearerToken = (token: string) => (this.bearerToken = token);

  addAuthHeader = (config: IConfig) => {
    if (this.bearerToken && !config.public)
      config.headers.Authorization = `Bearer ${this.bearerToken}`;
    delete config.public;
    return config;
  };

  // Security
  registerUserFromToken = async (
    token: string
  ): Promise<AxiosResponse<LoginResponse>> => {
    return await this.instance.get(`/auth/github/callback`, {
      params: { access_token: token },
    });
  };
  getLoggedInUser = async () =>
    await this.instance.get<LoginResponse>("/users/me");

  loginTestUser = async () => {
    if (process.env.NODE_ENV === "production") return null;
    try {
      //@ts-ignore
      return await this.instance.post(
        "/auth/local",
        { identifier: "testcodealo", password: "123456" },
        //@ts-ignore
        { public: true }
      );
    } catch (err) {}
  };

  // Users

  getUsernames = async () =>
    await this.instance.get<{ usernames: string[] }>("/users/usernames");
  getUser = async (username: string) =>
    await this.instance.get<User[]>(`/users`, { params: { username } });
  getUsersCount = async () => await this.instance.get<any>(`/users/count`);
  updateUser = async (id: number, update: Partial<User>) =>
    await this.instance.put(`/users/${id}`, update);
  updateUserWithUpload = async (
    id: string,
    data: FormData,
    onProgressChange: any
  ) =>
    await this.instance.put(`/users/${id}`, data, {
      timeoutErrorMessage: "El servidor tomo mucho tiempo en responder",
      onUploadProgress: (progressEvent) => {
        var percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgressChange(percentCompleted);
      },
    });
  setGitHubToken = async (token: string) =>
    await this.instance.put("/users/gh-token", { token });
  getTotalRegistered = async () => this.instance.get("/users/count");

  // Files

  uploadFile = async (data: FormData, onProgressChange: any): Promise<any> =>
    await this.instance.post("/upload", data, {
      timeoutErrorMessage: "El servidor tomo mucho tiempo en responder",
      onUploadProgress: (progressEvent) => {
        var percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgressChange(percentCompleted);
      },
    });
  // Newsletter
  updateNewsletter = async (action: "subscribe" | "unsubscribe") =>
    await this.instance.put(`/newsletter`, { action });

  // Single Pages
  getAboutUs = async () => await this.instance.get("/about-us");
  getStartHere = async () => await this.instance.get("/start-here");
  getPrivacyPolicy = async () => await this.instance.get("/privacy");
  getTermsAndConditions = async () =>
    await this.instance.get("/terms-and-conditions");

  // Common
  getJobPostFilters = async () => await this.instance.get("/job-post-filters");

  // Solutions
  getSolutions = async (params: object) =>
    await this.instance.get(`/solutions`, { params });
  getSolutionsForUser = async (id: string) =>
    await this.instance.get(`/solutions`, { params: { user: id } });
  getSolution = async (id: string) =>
    await this.instance.get(`/solutions/${id}`);
  updateSolution = async (id: string, data: Partial<Solution>) =>
    await this.instance.put(`/solutions/${id}`, data);
  startProject = async (projectId: string) =>
    await this.instance.post("/solutions", { project: projectId });
  getSolutionSlugs = async () => await this.instance.get("/solutions/slugs");
  getSolutionBySlug = async (slug: string) =>
    await this.instance.get(`/solutions/slugs/${slug}`);
  getForks = async () => await this.instance.get("/solutions/forks");

  // Comments
  postComment = async (dto: NewCommentDto, relation: string) =>
    await this.instance.post(`/comments/${relation}`, dto);
  updateVote = async ({ relation, action, commentId }) =>
    await this.instance.put(
      `/comments/${relation}/comment/${commentId}/votes`,
      { action }
    );

  // Job Posts
  getJobPosts = async () => await this.instance.get("/job-posts");
  getLanguages = async () => await this.instance.get("/languages");

  // Job Applications
  applyToJob = async (jobId: string, dto: { cv: string }) =>
    await this.instance.post(`/job-applications/${jobId}/apply`, dto);

  // Misc
  getProfileData = async () => await this.instance.get("/profile-data");
  getNewsletter = async () => await this.instance(`/proyect-newsletter`);

  // tags
  getProgrammingLevels = async () =>
    await this.instance.get("/programming-levels", {
      params: { _sort: "id:asc" },
    });
  getProgrammingBranches = async () =>
    await this.instance.get("/programming-branches", {
      params: { _sort: "id:asc" },
    });
  getProgrammingInterests = async () =>
    await this.instance.get("/programming-interests", {
      params: { _sort: "value:asc" },
    });

  // PLQQ
  storageQuestion = async (asking: object) =>
    await this.instance.post("/questions", asking);
}

const api = new Api();

export { api };
